/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      this.xCloudFilterCountObservers?.forEach((observer) => observer.disconnect());
      this.xCloudFilterCountRoots?.forEach((root) => {
        root.removeEventListener('input', this.xCloudFilterCountHandler);
        root.removeEventListener('change', this.xCloudFilterCountHandler);
        root.removeEventListener('click', this.xCloudFilterCountHandler);
      });
      this.xCloudFilterCountObservers = null;
      this.xCloudFilterCountRoots = null;
      this.xCloudFilterCountHandler = null;
      this.xCloudStateObserver?.disconnect();
      window.clearTimeout(this.xCloudFallbackTimeout);
      this.xCloudStateObserver = null;
      this.xCloudFallbackTimeout = null;
      this.xCloudResultScrollObserver?.disconnect();
      this.xCloudResultScrollSidebar?.removeEventListener('input', this.xCloudResultScrollHandler);
      this.xCloudResultScrollSidebar?.removeEventListener('change', this.xCloudResultScrollHandler);
      this.xCloudResultScrollSidebar?.removeEventListener('click', this.xCloudResultScrollHandler);
      window.clearTimeout(this.xCloudResultScrollTimeout);
      this.xCloudResultScrollObserver = null;
      this.xCloudResultScrollSidebar = null;
      this.xCloudResultScrollHandler = null;
      this.xCloudResultScrollTimeout = null;
      this.pendingXCloudResultScroll = false;
      this.xCloudResultGrid = null;

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.setupXCloudState();
      this.setupXCloudFilterCount();
      this.setupXCloudResultScroll();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    /**
     * Keeps native filters hidden while XCloud loads and restores them if the
     * expected sidebar markup does not appear. A late XCloud render can still
     * recover from the fallback state.
     */
    setupXCloudState() {
      if (this.dataset.useXcloudFilters !== 'true' || this.xCloudStateObserver) return;

      const sidebar = this.querySelector('#cloud_search_filters_sidebar');
      if (!sidebar) return;

      const markAsReady = () => {
        if (!sidebar.querySelector('.cloud-search-filters-sidebar')) return false;

        this.dataset.xcloudState = 'ready';
        window.clearTimeout(this.xCloudFallbackTimeout);
        this.xCloudStateObserver?.disconnect();
        this.xCloudStateObserver = null;
        this.xCloudFallbackTimeout = null;
        return true;
      };

      if (markAsReady()) return;

      this.dataset.xcloudState = 'loading';
      this.xCloudStateObserver = new MutationObserver(markAsReady);
      this.xCloudStateObserver.observe(sidebar, { childList: true, subtree: true });

      this.xCloudFallbackTimeout = window.setTimeout(() => {
        if (!markAsReady()) this.dataset.xcloudState = 'fallback';
        this.xCloudFallbackTimeout = null;
      }, 30000);
    }

    /**
     * Mirrors XCloud's applied-filter count in Enterprise's mobile filter button.
     * XCloud renders this value asynchronously and replaces its contents on updates.
     * Sorting is intentionally excluded: the Enterprise counter has always counted
     * only applied filters, not the selected sort order.
     */
    setupXCloudFilterCount() {
      const countElement = document.querySelector('[data-cloud-search-filter-count]');
      const xCloudRoots = [
        document.getElementById('cloud_search_filters_root'),
        document.getElementById('cloud_search_filters_sidebar')
      ].filter(Boolean);

      if (!countElement || xCloudRoots.length === 0 || this.xCloudFilterCountObservers) return;

      let syncTimeout;
      const syncCount = () => {
        window.clearTimeout(syncTimeout);
        syncTimeout = window.setTimeout(() => {
          const count = FacetFilters.getXCloudAppliedFilterCount(xCloudRoots);

          countElement.textContent = count;
          countElement.hidden = count === 0;
        }, 0);
      };

      this.xCloudFilterCountObservers = xCloudRoots.map((root) => {
        const observer = new MutationObserver(syncCount);
        observer.observe(root, { childList: true, subtree: true, characterData: true });
        return observer;
      });

      this.xCloudFilterCountHandler = syncCount;
      this.xCloudFilterCountRoots = xCloudRoots;
      xCloudRoots.forEach((root) => {
        root.addEventListener('input', this.xCloudFilterCountHandler);
        root.addEventListener('change', this.xCloudFilterCountHandler);
        root.addEventListener('click', this.xCloudFilterCountHandler);
      });

      syncCount();
    }

    /**
     * Scrolls to updated XCloud results only after a customer interaction.
     * The initial asynchronous XCloud render must not move the page.
     */
    setupXCloudResultScroll() {
      if (this.dataset.useXcloudFilters !== 'true' || this.xCloudResultScrollObserver) return;

      const sidebar = this.querySelector('#cloud_search_filters_sidebar');
      const reloadTarget = this.results?.querySelector('[data-cloud-search-reload]');
      if (!sidebar || !reloadTarget) return;

      this.xCloudResultScrollHandler = (evt) => {
        if (evt.type === 'click' && !evt.target.closest(
          '[data-filter-value], .cloud-search-clear-filters, .cloud-search-filters-apply'
        )) return;

        this.pendingXCloudResultScroll = true;
        this.xCloudResultGrid = this.getXCloudProductGrid();
      };
      this.xCloudResultScrollSidebar = sidebar;
      sidebar.addEventListener('input', this.xCloudResultScrollHandler);
      sidebar.addEventListener('change', this.xCloudResultScrollHandler);
      sidebar.addEventListener('click', this.xCloudResultScrollHandler);

      this.xCloudResultScrollObserver = new MutationObserver((records) => {
        if (!this.pendingXCloudResultScroll) return;

        const productGrid = this.getXCloudProductGrid();
        const gridWasUpdated = productGrid
          && (productGrid !== this.xCloudResultGrid
            || records.some((record) => record.target === productGrid || productGrid.contains(record.target)));

        if (!gridWasUpdated) return;

        window.clearTimeout(this.xCloudResultScrollTimeout);
        this.xCloudResultScrollTimeout = window.setTimeout(() => {
          this.pendingXCloudResultScroll = false;
          this.xCloudResultScrollTimeout = null;
          this.scrollToXCloudResults();
        }, 150);
      });
      this.xCloudResultScrollObserver.observe(reloadTarget, { childList: true, subtree: true });
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const usesXCloudFilters = this.dataset.useXcloudFilters === 'true';
        const isXCloudReady = usesXCloudFilters && this.dataset.xcloudState === 'ready';

        // XCloud owns the active filter state. Delegate sorting to its hidden
        // sort filter so it can update the product list without a page reload.
        if (usesXCloudFilters && evt.target.id?.includes('sort-by')) {
          const sortBy = evt.detail?.selectedValue || evt.target.value;

          if (isXCloudReady && FacetFilters.applyXCloudSort(sortBy)) return;

          // Fall back to a full reload if XCloud has not rendered its sort
          // control. The current URL preserves XCloud's active filter values.
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set('sort_by', sortBy);
          window.location.href = `${window.location.pathname}?${searchParams.toString()}`;
          return;
        }

      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);
      const emptyParams = [];

      if (this.sortingEnabled) {
        let currentSortBy = searchParams.get('sort_by');

        // Keep the mobile facets form sync'd with the desktop sort by dropdown
        if (evt.target.tagName === 'CUSTOM-SELECT') {
          this.mobileSortByOptions.forEach((option) => {
            option.checked = option.value === evt.detail.selectedValue;
            currentSortBy = evt.detail.selectedValue;
          });
        }

        // Set the 'sort_by' parameter.
        searchParams.set('sort_by', currentSortBy);
      }

      // Get empty parameters.
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });

      // Remove empty parameters.
      emptyParams.forEach((key) => {
        searchParams.delete(key);
      });

      this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
      const customPagination = document.querySelector('custom-pagination');
      if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
      this.results.classList.add('is-loading');

      // Disable "Show X results" button until submission is complete.
      const closeBtn = this.querySelector('.js-close-drawer-mob');
      closeBtn.ariaDisabled = 'true';
      closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

      // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

        // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

        // Update the label of the mobile filter button
        closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

        // Update the results.
        this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

        // Reinitialize any custom pagination
        if (customPagination && customPagination.reload) customPagination.reload();

        // Update the URL.
        if (updateUrl) FacetFilters.updateURL(searchParams);

        // Scroll to the top of the results if needed
        if (this.results.getBoundingClientRect().top < 0) {
          // If the header is sticky, compensate for it when scrolling to elements
          let headerHeight = 0;
          if (document.querySelector('store-header[data-is-sticky="true"]')) {
            headerHeight = Number.parseInt(
              getComputedStyle(this.parentElement)
                .getPropertyValue('--header-height')
                .replace('px', ''),
              10
            ) || 0;
          }
          window.scrollTo({
            top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
            behavior: 'smooth'
          });
        }

        // Enable the "Show X results" button
        closeBtn.classList.remove('is-loading');
        closeBtn.removeAttribute('aria-disabled');

        // Renable infinite scroll
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

        // Broadcast the update for anything else to hook into
        document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
      }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
      this.results.classList.remove('is-loading');
    }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }

    getXCloudProductGrid() {
      return this.results?.querySelector('[data-cloud-search-reload] > ul');
    }

    /**
     * Mirrors Enterprise's filter-scroll behaviour after XCloud has replaced
     * the product grid, without changing the original Enterprise code path.
     */
    scrollToXCloudResults() {
      if (!this.results || this.results.getBoundingClientRect().top >= 0) return;

      let headerHeight = 0;
      if (document.querySelector('store-header[data-is-sticky="true"]')) {
        headerHeight = Number.parseInt(
          getComputedStyle(this.parentElement)
            .getPropertyValue('--header-height')
            .replace('px', ''),
          10
        ) || 0;
      }

      window.scrollTo({
        top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
        behavior: 'smooth'
      });
    }

    /**
     * Applies sorting through XCloud's own hidden sort filter.
     * XCloud exposes Shopify-compatible sort values through data-filter-value
     * and names its corresponding radio inputs with the "sort-" prefix.
     * @param {string} sortBy - Shopify/XCloud sort value.
     * @returns {boolean} Whether an XCloud sort option was found.
     */
    static applyXCloudSort(sortBy) {
      const sortOptions = document.querySelectorAll(
        '#cloud_search_filters_sidebar [data-filter-value] input[name^="sort-"]'
      );
      const sortInput = Array.from(sortOptions).find(
        (input) => input.closest('[data-filter-value]')?.dataset.filterValue === sortBy
      );

      if (!sortInput) return false;

      const currentSortBy = new URLSearchParams(window.location.search).get('sort_by');

      // XCloud can leave its hidden radio checked after a history navigation.
      // Let the caller use a full reload when its internal state is stale.
      if (sortInput.checked && currentSortBy !== sortBy) return false;
      if (!sortInput.checked) sortInput.click();
      return true;
    }

    /**
     * Fallback when XCloud has not rendered its applied-filter counter yet.
     * URL parameters also cover range filters, which do not use checked inputs.
     * @returns {number} The number of selected, non-sort XCloud filter values.
     */
    static getXCloudAppliedFilterCount(xCloudRoots = []) {
      const selectedFilterCount = Array.from(document.querySelectorAll(
        '#cloud_search_filters_sidebar .cloud-search-filter-value--selected'
      ))
        .filter((value) => !value.querySelector('input[name^="sort-"]'))
        .length;

      // Once XCloud has rendered, its current DOM state is authoritative. The
      // app's reset action can leave native inputs checked even though their
      // selected state and URL filter have already been removed.
      if (xCloudRoots.some((root) => root.children.length > 0)) {
        return selectedFilterCount;
      }

      const urlFilterCount = Array.from(new URLSearchParams(window.location.search).keys())
        .filter((key) => key.startsWith('filter_'))
        .length;

      return urlFilterCount;
    }
  }

  customElements.define('facet-filters', FacetFilters);
}

// --- Global delegation for outside active-filter chips (single init) ---
(function () {
  // Prevent duplicate bindings in case this file is evaluated more than once
  if (window.__fdOutsideFilterDelegationBound__) return;
  window.__fdOutsideFilterDelegationBound__ = true;

  function getSearchParamsFromUrl(href) {
    try {
      const u = new URL(href, window.location.origin);
      return u.searchParams.toString();
    } catch (e) {
      const idx = href.indexOf('?');
      return idx > -1 ? href.slice(idx + 1) : '';
    }
  }

  document.addEventListener('click', async (evt) => {
    const link = evt.target.closest('.js-outside-active-filter, .js-outside-clear-all');
    if (!link) return;

    // Only intercept left-clicks (no modifier keys) to preserve default new-tab behavior
    if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;

    evt.preventDefault();

    const href = link.getAttribute('href') || link.href;
    const params = getSearchParamsFromUrl(href);

    const facetEl = document.querySelector('facet-filters');
    if (facetEl && typeof facetEl.applyFilters === 'function') {
      try {
        await facetEl.applyFilters(params, evt, true);
      } catch (e) {
        // Fallback to hard navigation if AJAX update fails
        window.location.href = href;
      }
    } else {
      // If the component is not present yet, do a normal navigation
      window.location.href = href;
    }
  }, { capture: true });
})();
